import type React from 'react';
import { useEffect, useState, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Search, Music, MapPin, Wrench, BadgeCheck, ArrowRight } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { getAdminUrl, getAssetUrl } from '@/utils/crossAppLinks';
import axios from 'axios';
import { getApiBaseUrl } from '@showliner/shared/services/apiConfig';
import { usePageTitle } from '@/hooks/usePageTitle';

type EntityType = 'performer' | 'venue' | 'service_provider';

interface EntityResult {
  id: string;
  name: string;
  bio?: string;
  description?: string;
  specialty?: string;
  profilePicture?: string;
  isVerified?: boolean;
  type: EntityType;
  city?: string;
  state?: string;
  genres?: string[];
}

const TYPE_CONFIG: Record<EntityType, { label: string; icon: React.ReactNode; route: (id: string) => string; squareAvatar: boolean }> = {
  performer: {
    label: 'Performer',
    icon: <Music className="mr-1 h-3 w-3" />,
    route: (id) => `/performer/${id}`,
    squareAvatar: false,
  },
  venue: {
    label: 'Venue',
    icon: <MapPin className="mr-1 h-3 w-3" />,
    route: (id) => `/venue/${id}`,
    squareAvatar: true,
  },
  service_provider: {
    label: 'Crew',
    icon: <Wrench className="mr-1 h-3 w-3" />,
    route: (id) => `/service-provider/${id}`,
    squareAvatar: false,
  },
};

const EntityCard: React.FC<{ entity: EntityResult }> = ({ entity }) => {
  const cfg = TYPE_CONFIG[entity.type];
  const description = entity.bio || entity.description || entity.specialty || '';

  return (
    <Link to={cfg.route(entity.id)} className="block group">
      <Card className="overflow-hidden border-border/50 hover:border-primary/30 hover:shadow-md transition-all duration-300">
        <CardContent className="p-4">
          <div className="flex items-start gap-4">
            <Avatar className={`h-14 w-14 flex-shrink-0 ${cfg.squareAvatar ? 'rounded-lg' : ''}`}>
              {entity.profilePicture ? (
                <AvatarImage
                  src={getAssetUrl(entity.profilePicture)}
                  alt={entity.name}
                  className={cfg.squareAvatar ? 'rounded-lg' : ''}
                />
              ) : null}
              <AvatarFallback className={`text-lg font-bold bg-primary text-white ${cfg.squareAvatar ? 'rounded-lg' : ''}`}>
                {entity.name.charAt(0).toUpperCase()}
              </AvatarFallback>
            </Avatar>

            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <h3 className="font-heading font-semibold text-grey-900 truncate group-hover:text-primary transition-colors">
                  {entity.name}
                </h3>
                {entity.isVerified && (
                  <BadgeCheck className="h-4 w-4 text-verified flex-shrink-0" />
                )}
              </div>
              <div className="flex items-center gap-2 mt-0.5">
                <Badge variant="secondary" className="text-xs py-0">
                  {cfg.icon}
                  {cfg.label}
                </Badge>
                {entity.city && (
                  <span className="text-xs text-muted-foreground">
                    {[entity.city, entity.state].filter(Boolean).join(', ')}
                  </span>
                )}
                {entity.type === 'service_provider' && entity.specialty && (
                  <span className="text-xs text-muted-foreground">
                    {entity.specialty}
                  </span>
                )}
              </div>
              {entity.genres && entity.genres.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-1.5">
                  {entity.genres.map((genre) => (
                    <Badge key={genre} variant="outline" className="text-xs py-0 font-normal">
                      {genre}
                    </Badge>
                  ))}
                </div>
              )}
              {description && (
                <p className="mt-2 text-sm text-muted-foreground line-clamp-2">
                  {description}
                </p>
              )}
            </div>

            <ArrowRight className="h-5 w-5 text-muted-foreground/40 group-hover:text-primary transition-colors flex-shrink-0 mt-1" />
          </div>
        </CardContent>
      </Card>
    </Link>
  );
};

const ResultSkeleton: React.FC = () => (
  <Card className="border-border/50">
    <CardContent className="p-4">
      <div className="flex items-start gap-4">
        <Skeleton className="h-14 w-14 rounded-full" />
        <div className="flex-1 space-y-2">
          <Skeleton className="h-5 w-32" />
          <Skeleton className="h-4 w-20" />
          <Skeleton className="h-4 w-full" />
        </div>
      </div>
    </CardContent>
  </Card>
);

type Tab = 'all' | 'performers' | 'venues' | 'crew';

const Discover: React.FC = () => {
  usePageTitle('Discover');
  const [searchTerm, setSearchTerm] = useState('');
  const [tab, setTab] = useState<Tab>('all');
  const [performers, setPerformers] = useState<EntityResult[]>([]);
  const [venues, setVenues] = useState<EntityResult[]>([]);
  const [crew, setCrew] = useState<EntityResult[]>([]);
  const [loading, setLoading] = useState(true);
  // Monotonic request id — a slow response for an old query must not
  // overwrite the results of a newer one (typing fast during the debounce).
  const requestSeq = useRef(0);

  const fetchResults = useCallback(async (query: string) => {
    const seq = ++requestSeq.current;
    setLoading(true);
    try {
      const baseUrl = getApiBaseUrl();
      const params = new URLSearchParams({ limit: '20' });
      if (query) params.set('q', query);

      const [performerRes, venueRes, crewRes] = await Promise.all([
        axios.get(`${baseUrl}/api/discover/performers?${params}`),
        axios.get(`${baseUrl}/api/discover/venues?${params}`),
        axios.get(`${baseUrl}/api/discover/service-providers?${params}`).catch(() => ({ data: { serviceProviders: [] } })),
      ]);

      if (seq !== requestSeq.current) return;

      setPerformers(
        (performerRes.data.performers || performerRes.data || []).map((p: EntityResult) => ({
          ...p,
          type: 'performer' as const,
        }))
      );
      setVenues(
        (venueRes.data.venues || venueRes.data || []).map((v: EntityResult) => ({
          ...v,
          type: 'venue' as const,
        }))
      );
      setCrew(
        (crewRes.data.serviceProviders || []).map((c: EntityResult) => ({
          ...c,
          type: 'service_provider' as const,
        }))
      );
    } catch {
      if (seq !== requestSeq.current) return;
      // Endpoints may not exist yet — show empty state
      setPerformers([]);
      setVenues([]);
      setCrew([]);
    } finally {
      if (seq === requestSeq.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchResults(searchTerm);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm, fetchResults]);

  const allResults = [...performers, ...venues, ...crew].sort((a, b) => a.name.localeCompare(b.name));
  const displayResults =
    tab === 'performers' ? performers :
    tab === 'venues' ? venues :
    tab === 'crew' ? crew :
    allResults;

  return (
    <div className="mx-auto max-w-4xl px-4 sm:px-8 page-top">
      {/* Header */}
      <div className="mb-10">
        <p className="label-stamp text-grey-700 mb-6">
          <span className="text-primary-600">●</span>&nbsp;&nbsp;Directory
        </p>
        <h1 className="font-display text-[clamp(2.5rem,6vw,4.5rem)] leading-[0.97] tracking-tight text-ink max-w-[14ch]">
          Who's on <span className="text-primary-600">Showliner.</span>
        </h1>
        <p className="mt-5 text-lg text-grey-800 leading-snug max-w-[40ch]">
          Browse the venues, the performers, and the crew on Showliner.
        </p>
      </div>

      {/* Search */}
      <div className="relative mb-8">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
        <Input
          type="text"
          placeholder="Search venues, performers, and crew…"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="h-12 pl-12 text-base rounded-md"
        />
      </div>

      {/* Tabs */}
      <Tabs value={tab} onValueChange={(v) => setTab(v as Tab)} className="mb-6">
        <TabsList className="grid w-full grid-cols-2 sm:grid-cols-4 h-auto">
          <TabsTrigger value="all">
            All ({allResults.length})
          </TabsTrigger>
          <TabsTrigger value="performers">
            <Music className="mr-1 h-4 w-4" /> Performers ({performers.length})
          </TabsTrigger>
          <TabsTrigger value="venues">
            <MapPin className="mr-1 h-4 w-4" /> Venues ({venues.length})
          </TabsTrigger>
          <TabsTrigger value="crew">
            <Wrench className="mr-1 h-4 w-4" /> Crew ({crew.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value={tab} className="mt-6">
          {loading ? (
            <div className="space-y-4">
              {[1, 2, 3, 4].map((i) => <ResultSkeleton key={i} />)}
            </div>
          ) : displayResults.length > 0 ? (
            <div className="space-y-3">
              {displayResults.map((entity) => (
                <EntityCard key={`${entity.type}-${entity.id}`} entity={entity} />
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <Search className="h-12 w-12 text-muted-foreground/40 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-grey-900 mb-1">
                {searchTerm ? 'No matches yet' : 'Nothing here yet'}
              </h3>
              <p className="text-sm text-muted-foreground">
                {searchTerm
                  ? `Nothing matching "${searchTerm}" yet`
                  : 'The directory is just opening up. Make a profile and be one of the first on it.'}
              </p>
              {!searchTerm && (
                <Button variant="outline" className="mt-4" asChild>
                  <a href={getAdminUrl('/register')}>
                    Make your profile
                  </a>
                </Button>
              )}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Discover;
